import { useDeferredValue } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Launch from '@mui/icons-material/Launch';
import { ButtonBase } from '@mui/material';

import SideDrawer from '@/components/SideDrawer';
import ExportDrawerEmptyState from '@/features/DataHub/components/ExportDrawer/EmptyState';
import { ExportConfigView } from '@/features/DataHub/components/ExportDrawer/ExportConfigView';
import { useExportHistory } from '@/features/DataHub/hooks/useExportHistory';
import { useOnOpen } from '@/features/DataHub/hooks/useOnOpen';
import {
  useExportMeta,
  useExportRequest
} from '@/features/DataHub/modules/Export/context/exportContexts';
import { PastExports } from '@/features/DataHub/pages/PastExports';

interface ExportDrawerProps {
  open: boolean;
  onClose: () => void;
}

const ExportDrawer = ({ open, onClose }: ExportDrawerProps) => {
  const { showHistory, toggleHistory } = useExportHistory();
  const { resetExportState } = useExportRequest();
  const { uniqueSymbols } = useExportMeta();

  const hasSymbols = uniqueSymbols.size > 0;

  const deferredHasSymbols = useDeferredValue(hasSymbols);
  const effectiveHasSymbols = open ? hasSymbols : deferredHasSymbols;

  useOnOpen(open, () => {
    resetExportState();
  });

  const renderContent = () => {
    if (showHistory) {
      return <PastExports />;
    }

    if (!effectiveHasSymbols) {
      return <ExportDrawerEmptyState />;
    }

    return <ExportConfigView openKey={open.toString()} />;
  };

  return (
    <SideDrawer
      before={
        showHistory ? (
          <ButtonBase
            disableRipple
            disableTouchRipple
            sx={{
              px: 2,
              height: '100%',
              backgroundColor: 'background.paper'
            }}
            onClick={toggleHistory}
          >
            <ArrowBackIcon />
          </ButtonBase>
        ) : undefined
      }
      icon={
        <Launch
          sx={{
            fontSize: 32
          }}
        />
      }
      isHistoryActive={showHistory}
      open={open}
      title='Export'
      onClose={onClose}
      onHistoryClick={toggleHistory}
    >
      {renderContent()}
    </SideDrawer>
  );
};

export default ExportDrawer;
